import { CAPABILITY } from "../helpers/permissions";

/**
 * Global creation actions that already have a workflow the user can start
 * without a project or location already on screen.
 *
 * `action` ids are dispatched by App.js into those existing openers.
 * Visibility uses capability checks, not role names.
 */
export const QUICK_CREATE_GROUPS = Object.freeze([
  {
    id: "work",
    label: "Work",
    actions: [
      {
        id: "client",
        title: "Client",
        subtitle: "New client",
        capability: CAPABILITY.CLIENT_CREATE,
        action: "new_client",
      },
      {
        id: "estimate",
        title: "Estimate",
        subtitle: "New estimate",
        capability: CAPABILITY.ESTIMATE_EDIT,
        action: "new_estimate",
      },
      {
        id: "project",
        title: "Project",
        subtitle: "New project",
        capability: CAPABILITY.PROJECT_CREATE,
        action: "new_project",
      },
      {
        id: "task",
        title: "Task",
        subtitle: "New catalog task",
        capability: CAPABILITY.TASK_CATALOG_EDIT,
        action: "new_task",
      },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    actions: [
      {
        id: "invoice",
        title: "Invoice",
        subtitle: "Standalone invoice",
        capability: CAPABILITY.INVOICE_REVIEW,
        action: "new_invoice",
      },
      {
        id: "payment",
        title: "Payment",
        subtitle: "Choose an open invoice",
        capability: CAPABILITY.INVOICE_MARK_PAID,
        action: "record_payment",
      },
      {
        id: "expense",
        title: "Expense",
        subtitle: "Record money out",
        capability: CAPABILITY.FINANCE_MANAGE,
        action: "money_out",
      },
    ],
  },
  {
    id: "time_field",
    label: "Time & Field",
    actions: [
      {
        id: "time",
        title: "Time Entry",
        subtitle: "Manual time log",
        capability: CAPABILITY.SCREEN_MANUAL_TIME_ENTRY,
        action: "new_time",
      },
      {
        id: "material",
        title: "Material",
        subtitle: "Catalog item",
        capability: CAPABILITY.MATERIAL_CATALOG_EDIT,
        action: "new_material",
      },
    ],
  },
]);

/**
 * Candidates audited and left contextual. Kept here so the launcher does not
 * grow a second copy of a workflow that cannot be finished from Home.
 */
export const QUICK_CREATE_EXCLUSIONS = Object.freeze([
  {
    id: "payable",
    reason:
      "Accounts Payable is a review of unpaid time and payroll liability. There is no create-payable workflow.",
  },
  {
    id: "project_task",
    reason:
      "Adding a task to a job requires a project. Quick Create opens the catalog task editor instead, which is the global task workflow.",
  },
  {
    id: "picture",
    reason:
      "Pictures are captured inside a project or location. There is no global picker that finishes that workflow.",
  },
  {
    id: "document",
    reason: "CrewPilot has no global document-creation workflow.",
  },
]);

export function visibleQuickCreateGroups(can) {
  const allow = typeof can === "function" ? can : () => false;

  return QUICK_CREATE_GROUPS.map((group) => ({
    ...group,
    actions: group.actions.filter((action) => allow(action.capability)),
  })).filter((group) => group.actions.length > 0);
}
